import { ReactElement, useState } from 'react';
import moment from 'moment';
import 'moment-timezone';
import 'react-datepicker/dist/react-datepicker.css';
import { Option, TimeSpan, TimeSpanDateAttributes } from '../constants/telosair-interfaces';
import { useData } from '../context/data.context';
import DropdownControl from '../controls/DropdownControl';
import DateRangePicker from '../controls/DateRangePicker';
import { Filters, setAppliedFilter } from '../services/localstorage.service';

type Props = {
  onChangeTimeFilter: (span: string | TimeSpan | null) => void;
  timeSpanDate: TimeSpanDateAttributes | null;
  setTimeSpanDate: (value: TimeSpanDateAttributes) => void;
  timeSpan: TimeSpan | null;
  theme?: 'bg-dark' | 'bg-light';
  hansNoneDate?: boolean;
};

/**
 * Date Range component, supports custom range picking.
 * @param props - Properties.
 * @returns - DateRange component.
 */
export default function DateRange(props: Props): ReactElement {
  const { onChangeTimeFilter, timeSpan, timeSpanDate, setTimeSpanDate, theme, hansNoneDate } =
    props;
  const [customDatePicker, setCustomDatePicker] = useState(false);
  const { userConfigData } = useData();
  /**
   * Calls onChangeTimeFilter conditionally.
   * @param type - Time span selected.
   */
  const handleOnTimeFilterChange = (type: TimeSpan | 'None'): void => {
    if (type === 'None') {
      onChangeTimeFilter(null);
      return;
    }
    if (type === TimeSpan.Custom) {
      setCustomDatePicker(true);
    } else {
      onChangeTimeFilter(type);
    }
  };

  /**
   * A function to select date range.
   * @param value - Selected start and end time.
   */
  function handleApply(value: TimeSpanDateAttributes): void {
    if (value.startDate) {
      setTimeSpanDate(value);
      setAppliedFilter({
        key: Filters.alertTimeSpanCustom,
        data: value
      });
    }
    onChangeTimeFilter(TimeSpan.Custom);
    setCustomDatePicker(false);
  }

  /**
   * A function to remove date range, updates the state setStartDate and setEndDate to null.
   */
  function handleCancel(): void {
    setCustomDatePicker(false);
    onChangeTimeFilter(timeSpan);
  }

  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  let TIME_SPAN_PLACEHOLDER = 'Date Range';
  if (timeSpan === TimeSpan.Custom) {
    TIME_SPAN_PLACEHOLDER = `${moment(timeSpanDate?.startDate)
      .tz(timeZone)
      .format('Do MMM')} - ${moment(timeSpanDate?.endDate)
      .tz(timeZone)
      .format('Do MMM')}`;
  } else if (timeSpan) {
    TIME_SPAN_PLACEHOLDER = timeSpan;
  } else {
    TIME_SPAN_PLACEHOLDER = 'Date Range';
  }

  const TIME_SPANS: Option<string>[] = Object.values(TimeSpan).map((item) => ({
    name: item,
    id: item
  }));
  if (hansNoneDate) {
    TIME_SPANS.push({ name: 'All', id: 'none' });
  }
  return (
    <>
      <DropdownControl
        controlPlaceholder={TIME_SPAN_PLACEHOLDER}
        options={TIME_SPANS}
        theme={theme}
        onSelect={(option) => handleOnTimeFilterChange(option.name as TimeSpan)}
      />
      {customDatePicker && (
        <div className="absolute z-50 top-11">
          <DateRangePicker
            timeSpanDate={timeSpanDate}
            handleCancel={() => handleCancel()}
            onApply={(value) => handleApply(value)}
          />
        </div>
      )}
    </>
  );
}
